import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type SegmentStatus, segmentStatus } from '@openx/types/segments';
import type { AudienceListType, SegmentTreeNode } from '../../../../../../../modules/audiences/AudienceForm/types';
import { daysToExpiration } from '../../../../../utils/daysToExpiration';
import { isSoonToBeExpired } from '../../../../../utils/isSoonToBeExpired';
import { SegmentAlert } from '../expandableSections/segments/SegmentAlert';
import { HoverableItem } from '../HoverableItem';
import { getSegmentAlertProps } from '../utils/getSegmentAlertProps';

type BaseLeafNodeProps = {
  node: SegmentTreeNode;
  type: AudienceListType;
};

type LeafNodeProps = BaseLeafNodeProps &
  ({ readOnly: true } | { readOnly?: false; onLeafDelete: (node: SegmentTreeNode) => void });

export const LeafNode = ({ node, type, ...rest }: LeafNodeProps) => {
  const showArchived = !!node.is_archived;
  const showExpired = node.status === segmentStatus.EXPIRED;
  const showExpiresSoon = isSoonToBeExpired(node?.expiration_date, node.status as SegmentStatus);
  const daysRemaining = daysToExpiration(node?.expiration_date) ?? undefined;

  const hoverableItemProps =
    rest.readOnly === true
      ? { readOnly: true as const }
      : {
          readOnly: false as const,
          onDelete: () => rest.onLeafDelete(node),
          isNested: true,
        };

  return (
    <HoverableItem type={type} {...hoverableItemProps}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {node.description ? (
            <Tooltip title={node.description} placement="top">
              <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                {node.name}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
              {node.name}
            </Typography>
          )}
        </Box>

        <SegmentAlert {...getSegmentAlertProps(showArchived, showExpired, showExpiresSoon, daysRemaining)} />
      </Box>
    </HoverableItem>
  );
};
